import { useEffect, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { useJsmAdminPermissionQuery } from './__generated__/useJsmAdminPermissionQuery.graphql.ts';

const jsmAdminPermissionQuery = graphql`
	query useJsmAdminPermissionQuery($cloudId: ID!) {
		jira {
			hasJsmAdminAccess(cloudId: $cloudId) @optIn(to: "JiraHasJsmAdminAccess")
		}
	}
`;

/**
 * The outcome of the JSM admin permission check, exposed to consumers as a single value so they can
 * switch on one status instead of juggling separate loading / error / result booleans.
 */
export const JsmAdminPermissionStatus = {
	/** The lookup has not resolved yet (also while the user identity is not hydrated). */
	Loading: 'loading',
	/** The user is a JSM/app admin. */
	Allowed: 'allowed',
	/** The lookup resolved and the user is not a JSM/app admin (also the gate-off result). */
	Denied: 'denied',
	/** The permission lookup failed. */
	Error: 'error',
} as const;

export type JsmAdminPermissionStatus =
	(typeof JsmAdminPermissionStatus)[keyof typeof JsmAdminPermissionStatus];

const toStatus = (permitted: boolean): JsmAdminPermissionStatus =>
	permitted ? JsmAdminPermissionStatus.Allowed : JsmAdminPermissionStatus.Denied;

const useJsmAdminPermissionEnabled = (): JsmAdminPermissionStatus => {
	const cloudId = useCloudId();
	const accountId = useAccountId();
	const environment = useRelayEnvironment();

	const [status, setStatus] = useState<JsmAdminPermissionStatus>(JsmAdminPermissionStatus.Loading);

	useEffect(() => {
		if (!accountId || !cloudId) {
			// Wait for a hydrated identity before querying, and reset to Loading if it is lost
			// mid-session so a previously resolved result is not left stale.
			setStatus(JsmAdminPermissionStatus.Loading);
			return;
		}

		// `store-or-network` lets Relay resolve straight from the store when a prior consumer has
		// already loaded this query: the observable then emits synchronously below, so `settled`
		// flips before the Loading fallback runs. Only a genuine cache miss reaches the network.
		let settled = false;

		const subscription = fetchQuery<useJsmAdminPermissionQuery>(
			environment,
			jsmAdminPermissionQuery,
			{ cloudId },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (data) => {
				settled = true;
				setStatus(toStatus(Boolean(data?.jira?.hasJsmAdminAccess)));
			},
			error: (error: unknown) => {
				settled = true;
				setStatus(JsmAdminPermissionStatus.Error);
				fireErrorAnalytics({
					meta: {
						id: 'fetchJsmAdminPermission',
						packageName: 'jiraServicedeskJsmAdminPermission',
						teamName: 'jsm-odyssey',
					},
					error: error instanceof Error ? error : new Error('Failed to fetch JSM admin permission'),
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		});

		// Cache miss: nothing emitted synchronously, so show Loading while the request is in flight.
		if (!settled) {
			setStatus(JsmAdminPermissionStatus.Loading);
		}

		// Unsubscribing on unmount / dependency change cancels the in-flight request so a stale
		// response cannot update the Relay store or this hook's state after it is no longer relevant.
		return () => {
			subscription.unsubscribe();
		};
	}, [accountId, cloudId, environment]);

	return status;
};

/**
 * Resolves whether the current user is a JSM/app admin, fetched from the AGG `hasJsmAdminAccess`
 * GraphQL field via Relay. Returns a {@link JsmAdminPermissionStatus}; resolves to `Denied` when the
 * gate is off.
 */
export const useJsmAdminPermission: () => JsmAdminPermissionStatus = functionWithCondition(
	() => fg('enable_jsm_admin'),
	useJsmAdminPermissionEnabled,
	(): JsmAdminPermissionStatus => JsmAdminPermissionStatus.Denied,
);
